/**
 * Markets Management Panel — Admin Dashboard
 *
 * View and edit market configuration. Changing launch_mode to 'live'
 * atomically sets landing_page_market_config.enabled = true so the
 * coming-soon gate opens instantly.
 *
 * API Endpoints:
 * - GET /api/v1/admin/markets          — List all markets
 * - POST /api/v1/admin/markets         — Create new market (SCRUM-1607)
 * - PATCH /api/v1/admin/markets/:id    — Update market (atomic dual-table)
 *
 * @story SCRUM-1469 — 0.1.1.10-01: Markets Management Panel
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import apiRequest from '../../services/api';

const LAUNCH_MODE_STYLES = {
  pre_launch: { label: 'Pre-Launch', color: '#f59e0b', bg: '#fef3c7' },
  live: { label: 'Live', color: '#10b981', bg: '#d1fae5' },
  closed: { label: 'Closed', color: '#6b7280', bg: '#f3f4f6' },
};

// ─── Confirmation Dialog ─────────────────────────────────────────────

function ConfirmDialog({ market, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
        maxWidth: '420px', width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
      }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '18px' }}>Confirm Go Live</h3>
        <p style={{ margin: '0 0 20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.5' }}>
          Are you sure you want to set <strong>{market.name}</strong> to <strong>live</strong>?
          This will make the marketplace accessible to all visitors in this market immediately.
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db',
              backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: 'none',
              backgroundColor: '#10b981', color: '#fff', cursor: 'pointer',
              fontSize: '14px', fontWeight: 600,
            }}
          >
            Set Live
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── New Market Modal ────────────────────────────────────────────────

function NewMarketModal({ onSubmit, onCancel }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [launchMode, setLaunchMode] = useState('pre_launch');
  const [approvalGate, setApprovalGate] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const autoSlug = name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const handleNameChange = (e) => {
    setName(e.target.value);
    if (!slugEdited) {
      setSlug('');
    }
  };

  const handleSlugChange = (e) => {
    setSlug(e.target.value);
    setSlugEdited(true);
  };

  const effectiveSlug = slugEdited && slug ? slug : autoSlug;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        name: name.trim(),
        slug: effectiveSlug,
        launch_mode: launchMode,
        approval_gate_enabled: approvalGate,
      });
    } catch (err) {
      setError(err.message || 'Failed to create market');
      setSubmitting(false);
    }
  };

  const inputStyle = {
    padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db',
    fontSize: '13px', width: '100%',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
        maxWidth: '480px', width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>New Market</h3>

        {error && (
          <div style={{
            padding: '8px 12px', backgroundColor: '#fee2e2', color: '#dc2626',
            borderRadius: '6px', marginBottom: '12px', fontSize: '13px',
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>
              Name *
            </label>
            <input type="text" value={name} onChange={handleNameChange}
              placeholder="e.g., Colorado Front Range" style={inputStyle} />
          </div>

          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>
              Slug {!slugEdited && autoSlug ? <span style={{ fontWeight: 400, color: '#6b7280' }}>(auto: {autoSlug})</span> : null}
            </label>
            <input type="text" value={slugEdited ? slug : autoSlug} onChange={handleSlugChange}
              placeholder="auto-generated-from-name" style={inputStyle} />
          </div>

          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>
              Launch Mode
            </label>
            <select value={launchMode} onChange={(e) => setLaunchMode(e.target.value)} style={inputStyle}>
              <option value="pre_launch">Pre-Launch</option>
              <option value="live">Live</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" checked={approvalGate} onChange={(e) => setApprovalGate(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
            <label style={{ fontSize: '13px', color: '#374151' }}>Approval Gate Enabled</label>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onCancel} style={{
            padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db',
            backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px',
          }}>Cancel</button>
          <button
            disabled={submitting || !name.trim()}
            onClick={handleSubmit}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: 'none',
              backgroundColor: '#3b82f6', color: '#fff',
              cursor: (submitting || !name.trim()) ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: 600,
              opacity: (submitting || !name.trim()) ? 0.6 : 1,
            }}
          >{submitting ? 'Creating...' : 'Create Market'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Market Row ──────────────────────────────────────────────────────

function MarketRow({ market, onSave }) {
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const hasEdits = Object.keys(edits).length > 0;
  const currentLaunchMode = edits.launch_mode !== undefined ? edits.launch_mode : market.launch_mode;
  const currentGate = edits.approval_gate_enabled !== undefined ? edits.approval_gate_enabled : market.approval_gate_enabled;
  const autoUrl = market.scheduler_id && !market.scheduling_url
    ? `${window.location.origin}/schedule/${market.slug}`
    : '';
  const currentUrl = edits.scheduling_url !== undefined ? edits.scheduling_url : (market.scheduling_url || autoUrl);

  const handleSave = async () => {
    // If changing to 'live', show confirmation dialog first
    if (edits.launch_mode === 'live' && market.launch_mode !== 'live') {
      setShowConfirm(true);
      return;
    }
    await doSave();
  };

  const doSave = async () => {
    setSaving(true);
    setShowConfirm(false);
    try {
      await onSave(market.id, edits);
      setEdits({});
    } finally {
      setSaving(false);
    }
  };

  const modeStyle = LAUNCH_MODE_STYLES[currentLaunchMode] || LAUNCH_MODE_STYLES.closed;

  return (
    <>
      {showConfirm && (
        <ConfirmDialog market={market} onConfirm={doSave} onCancel={() => setShowConfirm(false)} />
      )}
      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
        <td style={{ padding: '12px 8px' }}>
          <div style={{ fontWeight: 600 }}>{market.name}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>{market.slug}</div>
        </td>
        <td style={{ padding: '12px 8px' }}>
          <select
            value={currentLaunchMode}
            onChange={(e) => setEdits({ ...edits, launch_mode: e.target.value })}
            style={{
              padding: '6px 10px', borderRadius: '4px', border: '1px solid #d1d5db',
              backgroundColor: modeStyle.bg, color: modeStyle.color, fontWeight: 600, fontSize: '13px',
            }}
          >
            <option value="pre_launch">Pre-Launch</option>
            <option value="live">Live</option>
            <option value="closed">Closed</option>
          </select>
        </td>
        <td style={{ padding: '12px 8px' }}>
          <span style={{
            padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
            backgroundColor: market.lander_enabled ? '#d1fae5' : '#fee2e2',
            color: market.lander_enabled ? '#065f46' : '#dc2626',
          }}>
            {market.lander_enabled ? 'Open' : 'Gated'}
          </span>
        </td>
        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
          <input
            type="checkbox"
            checked={currentGate}
            onChange={(e) => setEdits({ ...edits, approval_gate_enabled: e.target.checked })}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
        </td>
        <td style={{ padding: '12px 8px' }}>
          <input
            type="text"
            value={currentUrl}
            onChange={(e) => setEdits({ ...edits, scheduling_url: e.target.value })}
            placeholder={market.scheduler_id ? 'Auto-generated from slug' : 'No scheduler connected'}
            style={{
              padding: '6px 10px', borderRadius: '4px', border: '1px solid #d1d5db',
              width: '100%', fontSize: '13px',
              ...(currentUrl === autoUrl && autoUrl ? { color: '#6b7280', fontStyle: 'italic' } : {}),
            }}
          />
        </td>
        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
          <span style={{
            width: '10px', height: '10px', borderRadius: '50%', display: 'inline-block',
            backgroundColor: market.active ? '#10b981' : '#ef4444',
          }} />
        </td>
        <td style={{ padding: '12px 8px' }}>
          {hasEdits && (
            <button
              disabled={saving}
              onClick={handleSave}
              style={{
                padding: '6px 14px', borderRadius: '4px', border: 'none',
                backgroundColor: '#3b82f6', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '13px', fontWeight: 600, opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          )}
        </td>
      </tr>
    </>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

export default function MarketsPanel() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);

  // SCRUM-1624: Global pre-launch gate state
  const [globalGateEnabled, setGlobalGateEnabled] = useState(false);
  const [gateLoading, setGateLoading] = useState(true);
  const [gateSaving, setGateSaving] = useState(false);
  const [gateStatus, setGateStatus] = useState(null);
  const [showGateConfirm, setShowGateConfirm] = useState(false);

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const fetchMarkets = useCallback(async () => {
    try {
      const response = await apiRequest('/api/v1/admin/markets');
      if (response.success) {
        setMarkets(response.data);
        setError(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load markets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMarkets();
  }, [fetchMarkets]);

  // SCRUM-1624: Fetch global gate state on mount
  useEffect(() => {
    (async () => {
      try {
        const response = await apiRequest('/api/v1/admin/pre-launch/config');
        if (response.success) {
          setGlobalGateEnabled(response.data.enabled);
        }
      } catch (_err) {
        // Gate state fetch failed — leave default (false)
      } finally {
        setGateLoading(false);
      }
    })();
  }, []);

  // SCRUM-1624: Toggle global pre-launch gate
  const handleGateToggle = (newValue) => {
    if (newValue === true) {
      setShowGateConfirm(true);
      return;
    }
    doGateToggle(false);
  };

  const doGateToggle = async (newValue) => {
    setShowGateConfirm(false);
    setGateSaving(true);
    try {
      const response = await apiRequest('/api/v1/admin/pre-launch/config', {
        method: 'PUT',
        body: { enabled: newValue },
      });
      if (response.success) {
        setGlobalGateEnabled(response.data.enabled);
        setGateStatus('saved');
        setTimeout(() => setGateStatus(null), 3000);
      } else {
        setGateStatus('error');
        setTimeout(() => setGateStatus(null), 5000);
      }
    } catch (_err) {
      setGateStatus('error');
      setTimeout(() => setGateStatus(null), 5000);
    } finally {
      setGateSaving(false);
    }
  };

  const handleSave = async (id, updates) => {
    try {
      const response = await apiRequest(`/api/v1/admin/markets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.success) {
        setToast(`${response.data.name} updated`);
        setTimeout(() => setToast(null), 3000);
        await fetchMarkets();
      }
    } catch (err) {
      setToast(`Save failed: ${err.message}`);
      setTimeout(() => setToast(null), 5000);
      throw err;
    }
  };

  const handleCreate = async (fields) => {
    const response = await apiRequest('/api/v1/admin/markets', {
      method: 'POST',
      body: JSON.stringify(fields),
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.success) {
      setToast(`${response.data.name} created`);
      setTimeout(() => setToast(null), 3000);
      setShowNewModal(false);
      await fetchMarkets();
    }
  };

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="markets"
      onNavigate={handleNavigate}
      user={{
        name: authUser?.fullName || authUser?.firstName || 'Admin',
        email: authUser?.email || '',
        avatar: authUser?.profilePicture || null
      }}
    >
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Markets Management</h1>
        <button
          onClick={() => setShowNewModal(true)}
          style={{
            padding: '8px 16px', borderRadius: '6px', border: 'none',
            backgroundColor: '#3b82f6', color: '#fff', cursor: 'pointer',
            fontSize: '14px', fontWeight: 600,
          }}
        >New Market</button>
      </div>

      {showNewModal && (
        <NewMarketModal onSubmit={handleCreate} onCancel={() => setShowNewModal(false)} />
      )}

      {/* SCRUM-1624: Gate enable confirmation */}
      {showGateConfirm && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
            maxWidth: '420px', width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '18px' }}>Enable Global Pre-Launch Gate</h3>
            <p style={{ margin: '0 0 20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.5' }}>
              This will gate the entire platform for unauthenticated users.
              All visitors will be redirected to the coming-soon page.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowGateConfirm(false)}
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db',
                  backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px',
                }}
              >Cancel</button>
              <button
                onClick={() => doGateToggle(true)}
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: 'none',
                  backgroundColor: '#f59e0b', color: '#fff', cursor: 'pointer',
                  fontSize: '14px', fontWeight: 600,
                }}
              >Enable Gate</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', top: '16px', right: '16px', padding: '12px 20px',
          borderRadius: '8px',
          backgroundColor: toast.startsWith('Save failed') ? '#fee2e2' : '#d1fae5',
          color: toast.startsWith('Save failed') ? '#dc2626' : '#065f46',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000, fontSize: '14px',
        }}>
          {toast}
        </div>
      )}

      {error && (
        <div style={{ padding: '16px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '8px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* SCRUM-1624: Global Pre-Launch Gate Toggle */}
      <div style={{
        marginBottom: '16px', padding: '12px 16px', backgroundColor: '#f8f9fa',
        borderRadius: '6px', border: '1px solid #e9ecef',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label style={{ fontWeight: 600, fontSize: '14px' }}>Global Pre-Launch Gate</label>
          {gateLoading ? (
            <span style={{ fontSize: '13px', color: '#6c757d' }}>Loading...</span>
          ) : (
            <input
              type="checkbox"
              checked={globalGateEnabled}
              disabled={gateSaving}
              onChange={(e) => handleGateToggle(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: gateSaving ? 'not-allowed' : 'pointer' }}
            />
          )}
          {gateSaving && <span style={{ fontSize: '13px', color: '#6c757d' }}>Saving...</span>}
          {gateStatus === 'saved' && <span style={{ fontSize: '13px', color: '#28a745' }}>Saved</span>}
          {gateStatus === 'error' && <span style={{ fontSize: '13px', color: '#dc3545' }}>Save failed</span>}
        </div>
        <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6c757d' }}>
          When enabled, unauthenticated users are redirected to the coming-soon page on all platform routes.
        </p>
      </div>

      <div style={{
        padding: '12px 16px', backgroundColor: '#eff6ff', borderRadius: '8px',
        marginBottom: '16px', fontSize: '13px', color: '#1d4ed8',
      }}>
        Changing launch mode to &quot;Live&quot; will immediately open the coming-soon gate for that market.
        &quot;Pre-Launch&quot; or &quot;Closed&quot; will gate the market behind the coming-soon page.
      </div>

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
      ) : markets.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>No markets found.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: '8px' }}>Market</th>
              <th style={{ padding: '8px' }}>Launch Mode</th>
              <th style={{ padding: '8px' }}>Lander Gate</th>
              <th style={{ padding: '8px', textAlign: 'center' }}>Approval Gate</th>
              <th style={{ padding: '8px' }}>Scheduling URL</th>
              <th style={{ padding: '8px', textAlign: 'center' }}>Active</th>
              <th style={{ padding: '8px' }}></th>
            </tr>
          </thead>
          <tbody>
            {markets.map((m) => (
              <MarketRow key={m.id} market={m} onSave={handleSave} />
            ))}
          </tbody>
        </table>
      )}
    </div>
    </DashboardLayout>
  );
}
